import { RegisteredComponent } from "@builder.io/sdk-react";
import { SORTING_OPTION } from "../../commonFields";
import { IndexCardsContextBlockProps } from "./IndexContextBlock";
import { LocaleIsoCode } from "@/types";
import {
  retrieveIndexCards,
  getSearchFieldsByVariant,
} from "@/helpers/api/builder";
import { createSortingMapping } from "@/helpers";
import { remapProps as ImageRemapProps } from "@/libs/builder/blocks/ImageBlock.builder";

export const indexContextBlockConfig: Omit<RegisteredComponent, "component"> = {
  name: "Index cards context",
  canHaveChildren: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderContext: true,
  },
  description:
    "Abstraction for MongoDB queries - only server side sorting works here, for more complex sorting, check the sections' sorting.",
  inputs: [
    {
      name: "paginationType",
      type: "enum",
      enum: ["infinite", "expandable"],
    },
    {
      name: "numberOfPaginatedCards",
      type: "number",
      defaultValue: 10,
      helperText:
        "This field indicates how many cards are retrieved when paginating.",
    },
    {
      name: "initialSorting",
      type: "object",
      helperText:
        "Defines the sorting for the first render. Choose the card type, and the fields available will be displayed for the given card type.",
      subFields: SORTING_OPTION,
      localized: false,
    },
    {
      name: "variant",
      type: "enum",
      enum: [
        "Resource",
        "Event",
        "Use Case",
        "Integration",
        "Product",
        "Newsroom",
        "CVE",
      ],
      localized: false,
      required: false,
      helperText:
        "The type of index cards. Determines which fields are searched by the search expression.",
    },
    {
      name: "searchExpression",
      type: "text",
      localized: false,
    },
    {
      name: "areAllCardsInMarkup",
      type: "boolean",
      defaultValue: true,
      helperText:
        "This flag dictates whether all of the cards (regardless of pagination) are part of the markup. This flag should be enabled if all cards need to be seen by the SERP.",
    },
  ],
};

export const remapProps = async (
  props: IndexCardsContextBlockProps,
  url: string,
  isoLocale: LocaleIsoCode,
  searchParamsPromise: Promise<Record<string, string>>,
) => {
  const searchParams = await searchParamsPromise;
  const searchFields = getSearchFieldsByVariant(props.variant);
  const sortingFn = props.initialSorting
    ? createSortingMapping([props.initialSorting])?.[0]
    : null;
  const { cards } = await retrieveIndexCards(
    url,
    isoLocale,
    [],
    undefined,
    sortingFn,
    searchFields,
  );
  const searchExpressionQuery = searchParams["searchTerm"]?.toLowerCase();
  const filterQueryParam: Record<string, string[]> = {};
  for (const category of cards.flatMap(({ data }) =>
    data.categorization?.map(({ parameterName, categories }) => ({
      parameterName,
      categories,
    })),
  )) {
    if (category && searchParams[category.parameterName]) {
      if (!(category.parameterName in filterQueryParam)) {
        filterQueryParam[category.parameterName] =
          searchParams[category.parameterName].split(",");
      }
    }
  }
  const filteredCards = cards.filter(
    (card) =>
      (searchExpressionQuery
        ? searchFields.some(({ fieldPath }) => {
            const dataKey = fieldPath.replace("data.", "");
            const value = card.data[dataKey as keyof typeof card.data];
            return (
              typeof value === "string" &&
              value.toLowerCase().includes(searchExpressionQuery)
            );
          })
        : true) &&
      (Object.keys(filterQueryParam).length
        ? card.data.categorization?.some(
            (categorization) =>
              categorization.parameterName in filterQueryParam &&
              filterQueryParam[categorization.parameterName].some((filter) =>
                categorization.categories?.some((category) =>
                  category.endsWith(filter),
                ),
              ),
          )
        : true),
  );

  return {
    ...props,
    cards: filteredCards,
    numberOfResults: filteredCards.length,
    filters: cards,
  };
};
